<template>
  <m-dropdown
    v-model:value="showEditor"
    class="rich-text-input"
    :title="title"
    placement="onTopLeft"
    block
    :disabled="disabled || readOnly"
    :match-trigger-width="matchTriggerWidth"
    @hide="hide"
    @click.stop
  >
    <m-focusable
      :class="['_editor-content', { '-wrap': wrap }, { '-hide-hover': hideHover }]"
      :hide-border="hideBorder"
      :small="small"
      full-width
      :hide-hover="hideHover"
      :read-only="readOnly"
      :disabled="disabled"
      :m-style="mStyle"
    >
      <m-editor
        :initial-value="content"
        disabled
        :allowed-content="allowedContent"
        :placeholder="placeholder"
        light-placeholder
        :wrap="wrap"
        :default-font-size="resolveStyles(mStyle).fontSize"
      />
    </m-focusable>
    <template #overlay>
      <m-card
        no-padding
        :style="matchTriggerWidth ? { } : { width: '30rem' }"
        border-radius="small"
      >
        <m-focusable
          hide-border
          hide-hover
          hide-placeholder
          full-width
          :class="['_editor-content', { '-wrap': wrap }]"
        >
          <m-editor
            :initial-value="content"
            :allowed-content="allowedContent"
            :placeholder="placeholder"
            light-placeholder
            wrap
            full-width
            auto-focus
            :default-font-size="resolveStyles(mStyle).fontSize"
            :style="{ width: '100%' }"
            @input="updateContent"
          />
        </m-focusable>
      </m-card>
    </template>
  </m-dropdown>
</template>

<script setup>
import MEditor from '@/components/editor/MEditor.vue';
import useAccess from '@/composables/access/access';
import useDebounce from 'shared/composables/debounce';
import { editorNodeType, featureFlag } from 'shared/constants.json';
import { isEqual } from 'lodash-es';
import { mStyleProps, resolveStyles } from 'shared/lib/m-style-props';
import { onMounted, ref, toRef, watch } from 'vue';

const props = defineProps({
  ...mStyleProps,
  title: {
    type: String,
    default: '',
  },
  value: {
    type: Object,
    default: null,
  },
  placeholder: {
    type: String,
    default: '',
  },
  small: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  hideBorder: {
    type: Boolean,
    default: false,
  },
  hideHover: {
    type: Boolean,
    default: false,
  },
  matchTriggerWidth: {
    type: Boolean,
    default: false,
  },
  autoFocus: {
    type: Boolean,
    default: false,
  },
  wrap: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['change', 'blur']);

const { accountHasFeature } = useAccess();
const allowedContent = [
  editorNodeType.bulletList,
  editorNodeType.orderedList,
  editorNodeType.todoList,
];
if (accountHasFeature([featureFlag.fileUpload])) {
  allowedContent.push(editorNodeType.image, editorNodeType.file);
}

const content = ref(props.value);

const showEditor = ref(false);
const open = () => {
  showEditor.value = true;
};

const changeContent = (value) => {
  if (isEqual(value, props.value)) {
    return;
  }
  emit('change', value);
};

const { debounce, flushAll } = useDebounce({ onBeforeUnmountFlush: true });
const updateContent = (value) => {
  content.value = value;
  debounce(() => changeContent(value), 700, 'change');
};

const hide = () => {
  emit('blur');
  flushAll();
};

watch(toRef(props, 'value'), (value) => {
  if (isEqual(value, content.value)) {
    return;
  }
  content.value = value;
});

onMounted(() => {
  if (props.autoFocus) {
    open();
  }
});

defineExpose({ open });
</script>

<style scoped lang="scss" type="text/scss">
._editor-content {
  padding: .3rem .8rem;

  &.-hide-hover {
    padding: .6rem .8rem;
  }

  &.-wrap {
    display: block;
    height: 100%;
    max-height: 40rem;
    overflow: auto;
  }

  .m-editor {
    width: 100%;

    p.is-editor-empty {
      min-height: 0;
      padding: 0;
    }
  }
}
</style>
